import { useEffect } from "react";
import { useCartStore } from "@/stores/cartStore";
import { getSupabase } from "@/integrations/supabase/lazy";

interface CartAbandonmentProps {
  onAbandonmentDetected?: () => void;
}

export const useCartAbandonmentTracker = ({ onAbandonmentDetected }: CartAbandonmentProps = {}) => {
  const { items, cartId } = useCartStore();
  
  useEffect(() => {
    if (items.length === 0) return;

    const sessionId = sessionStorage.getItem('session_id') || generateSessionId();
    sessionStorage.setItem('session_id', sessionId);
    
    const totalValue = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
    
    // Track cart session
    const trackSession = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          await supabase
            .from('cart_sessions' as any)
            .upsert({
              session_id: sessionId,
              user_id: user.id,
              cart_data: { items, cartId },
              email: user.email || null,
              total_value: totalValue,
              is_abandoned: false,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'session_id' });
        } else {
          await supabase.rpc('upsert_anonymous_cart_session' as any, {
            p_session_id: sessionId,
            p_cart_data: { items, cartId },
            p_total_value: totalValue,
            p_email: null,
          });
        }

        // Report the cart to Klaviyo so its abandoned-cart flow can email the
        // shopper. Only possible when we know the shopper's email (signed in,
        // or saved earlier via the "save this cart" box).
        const email = user?.email || localStorage.getItem('loftloom-cart-email');
        if (email) {
          const { checkoutUrl } = useCartStore.getState();
          supabase.functions.invoke('klaviyo-track-cart', {
            body: {
              email,
              checkoutUrl,
              items: items.map((i) => ({
                title: i.product.node.title,
                handle: i.product.node.handle,
                price: parseFloat(i.price.amount),
                quantity: i.quantity,
                imageUrl: i.product.node.images?.edges?.[0]?.node?.url,
              })),
            },
          }).catch((err) => console.error('Klaviyo cart event failed:', err));
        }
      } catch (err) {
        console.error('Failed to track cart session:', err);
      }
    };

    trackSession();
  }, [items, cartId]);

  useEffect(() => {
    // Detect abandonment after 10 minutes of inactivity
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const resetTimeout = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (items.length > 0) {
          markAsAbandoned();
          onAbandonmentDetected?.();
        }
      }, 10 * 60 * 1000); // 10 minutes
    };

    const markAsAbandoned = async () => {
      const sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) return;

      try {
        const supabase = await getSupabase();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('cart_sessions' as any)
            .update({
              is_abandoned: true,
              updated_at: new Date().toISOString(),
            })
            .eq('session_id', sessionId)
            .eq('is_abandoned', false);
        } else {
          await supabase.rpc('mark_anonymous_cart_abandoned' as any, {
            p_session_id: sessionId,
          });
        }
      } catch (err) {
        console.error('Failed to mark cart as abandoned:', err);
      }
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    const activityHandler = () => resetTimeout();
    
    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });

    resetTimeout();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => {
        document.removeEventListener(event, activityHandler, true);
      });
    };
  }, [items, onAbandonmentDetected]);
};

const generateSessionId = () => {
  return `sess_${Date.now()}_${Math.random().toString(36).substring(2)}`;
};